import { useEffect, useRef, useCallback } from 'react';

const DEFAULT_RETRY_DELAY = 5000;
const MAX_RETRY_DELAY = 60000;
const MAX_RETRIES = 10;
const RETRY_JITTER_RATIO = 0.2;

/**
 * Custom hook for Server-Sent Events (SSE) connections.
 *
 * Uses the Fetch API with ReadableStream to support custom headers (e.g. JWT auth),
 * since the native EventSource API does not support custom headers.
 *
 * @param {string} url - SSE endpoint path (relative to REACT_APP_API_ENDPOINT)
 * @param {object} options
 * @param {function} options.onMessage - Callback when a message is received
 * @param {function} [options.onError] - Callback on error
 * @param {function} [options.onOpen] - Callback when connection opens
 * @param {boolean} [options.enabled=true] - Whether the connection should be active
 */
export default function useSSE(url, { onMessage, onError, onOpen, enabled = true } = {}) {
  const abortRef = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);
  const onOpenRef = useRef(onOpen);

  onMessageRef.current = onMessage;
  onErrorRef.current = onError;
  onOpenRef.current = onOpen;

  const connect = useCallback(() => {
    if (!mountedRef.current) return;

    const token = JSON.parse(localStorage.getItem('token'));

    if (!token) return;

    const controller = new AbortController();

    abortRef.current = controller;

    const baseURL = process.env.REACT_APP_API_ENDPOINT;
    const fullUrl = `${baseURL}/${url}`;

    fetch(fullUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`SSE connection failed: ${response.status}`);
        }

        retryCountRef.current = 0;
        onOpenRef.current?.();

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const read = () => {
          reader
            .read()
            .then(({ done, value }) => {
              if (done || !mountedRef.current) {
                if (mountedRef.current) scheduleRetry();

                return;
              }

              buffer += decoder.decode(value, { stream: true });

              const lines = buffer.split('\n');

              buffer = lines.pop();

              let currentEvent = null;
              let currentData = '';

              lines.forEach(line => {
                if (line.startsWith('event:')) {
                  currentEvent = line.slice(6).trim();
                } else if (line.startsWith('data:')) {
                  currentData += line.slice(5).trim();
                } else if (line === '') {
                  if (currentData) {
                    try {
                      const parsed = JSON.parse(currentData);

                      onMessageRef.current?.({ event: currentEvent, data: parsed });
                    } catch {
                      onMessageRef.current?.({ event: currentEvent, data: currentData });
                    }
                  }

                  currentEvent = null;
                  currentData = '';
                }
              });

              read();
            })
            .catch(err => {
              if (err.name !== 'AbortError' && mountedRef.current) {
                onErrorRef.current?.(err);
                scheduleRetry();
              }
            });
        };

        read();
      })
      .catch(err => {
        if (err.name !== 'AbortError' && mountedRef.current) {
          onErrorRef.current?.(err);
          scheduleRetry();
        }
      });
  }, [url]);

  const scheduleRetry = useCallback(() => {
    if (retryCountRef.current >= MAX_RETRIES || !mountedRef.current) return;

    const delay = Math.min(DEFAULT_RETRY_DELAY * 2 ** retryCountRef.current, MAX_RETRY_DELAY);
    const jitter = delay * RETRY_JITTER_RATIO * (Math.random() * 2 - 1);
    const jitteredDelay = Math.max(0, Math.round(delay + jitter));

    retryCountRef.current += 1;

    retryTimerRef.current = setTimeout(() => {
      if (mountedRef.current) connect();
    }, jitteredDelay);
  }, [connect]);

  const disconnect = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    clearTimeout(retryTimerRef.current);
    retryCountRef.current = 0;
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      connect();
    }

    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return { disconnect };
}
